export class AutotranslatorAudioContextError extends Error {
    constructor(readonly reason: "closed" | "resume_failed" | "clock_stalled") {
        super(`Audio context ${reason}`);
    }
}

// Silence is valid input. Verify the engine's clock, never require speech or a
// nonzero analyser sample to consider the microphone pipeline ready.
export function waitForAutotranslatorAudioContext(
    context: Pick<AudioContext, "state" | "currentTime" | "resume">,
    signal: AbortSignal,
    timeoutMs: number
): Promise<number> {
    return new Promise((resolve, reject) => {
        const initialTime = context.currentTime;
        let resumed = false;
        let settled = false;
        const finish = (error?: unknown) => {
            if (settled) return;
            settled = true;
            clearInterval(poll);
            clearTimeout(deadline);
            signal.removeEventListener("abort", aborted);
            if (error) reject(error);
            else resolve(Math.max(0, (context.currentTime - initialTime) * 1000));
        };
        const aborted = () => finish(signal.reason ?? new DOMException("Stopped", "AbortError"));
        const check = () => {
            if (signal.aborted) return aborted();
            if (context.state === "closed") return finish(new AutotranslatorAudioContextError("closed"));
            if (resumed && context.state === "running" && context.currentTime > initialTime) finish();
        };
        const poll = setInterval(check, 40);
        const deadline = setTimeout(() => finish(new AutotranslatorAudioContextError("clock_stalled")), timeoutMs);
        signal.addEventListener("abort", aborted, { once: true });
        if (signal.aborted) { aborted(); return; }
        try {
            void context.resume().then(() => { resumed = true; check(); }, () => finish(new AutotranslatorAudioContextError("resume_failed")));
        } catch { finish(new AutotranslatorAudioContextError("resume_failed")); }
    });
}
